import type {ReactElement} from 'react';
import React from 'react';
import BottomBar from '@Components/contextual-menu/components/bottom-bar';
import {openPosterEditorItemOpacityModal} from '@Modals/poster-editor-item-opacity-modal';
import {
  deleteItemByID,
  duplicateActiveItems,
  onCopyStylesClicked,
  showCopyStylesAsNewFeature,
  toggleLockStateForSelectedItem,
} from '@Components/poster-editor/library/poster-editor-library';
import {openPosterEditorItemPositionModal} from '@Modals/poster-editor-item-position-modal/poster-editor-item-position-modal';
import {openPosterEditorHyperlinkModal} from '@Modals/poster-editor-item-hyperlink-modal';
import {openPosterEditorItemStretchModal} from '@Modals/poster-editor-item-stretch-modal';
import {openPosterEditorItemFlipModal} from '@Modals/poster-editor-item-flip-modal';
import {openPosterEditorShadowModal} from '@Modals/poster-editor-item-shadow-modal';
import {openPosterEditorEditFancyTextModal} from '@Modals/poster-editor-fancy-text-modal';
import {openPosterEditorFancyTextEffectModal} from '@Modals/poster-editor-fancy-text-effect-modal';
import {openPosterEditorFancyTextFontsModal} from '@Modals/poster-editor-fancy-text-fonts-modal';
import {openPosterEditorFancyTextShapeModal} from '@Modals/poster-editor-fancytext-shape-modal';
import {openPosterEditorFancyTextColorsModal} from '@Modals/poster-editor-fancy-text-colors-modal';
import {useEditorPrefetchFancyTextMobileModules} from '@Hooks/poster-editor/useEditorPrefetchFancyTextMobileModules';
import {FancyTextItemBottomBarOptions} from '@Components/poster-editor/components/poster-editor-bottom-bar/poster-editor-bottom-bar.types';
import {useAppSelector} from '@/hooks';
import {getCopiedStyles} from '@Components/poster-editor/poster-editor-reducer';
import {CopyStyleState} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-item-controls/poster-item-controls.types';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetKeyFromActiveItem} from '@Hooks/poster-editor/useGetKeyFromActiveItem';
import {PulsatingDot} from '@Components/poster-editor/components/pulsating-dot';
import styles from './fancy-text-bottom-bar.module.scss';
import {openItemAnimationModal} from '@Modals/poster-editor-item-animation-modal';

export function FancyTextBottomBar(): ReactElement<any> | null {
  const copiedStylesState = useAppSelector(getCopiedStyles) ? CopyStyleState.COPIED : CopyStyleState.COPY;
  const activeItemId = useSingleActivePosterItemId();
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const isLocked = useGetKeyFromActiveItem('lockMovementX');
  useEditorPrefetchFancyTextMobileModules();

  if (!activeItem?.isFancyText()) {
    return null;
  }

  const pulsatingDotForCopyStyles = (): ReactElement<any> | null => {
    if (showCopyStylesAsNewFeature()) {
      return <PulsatingDot containerClassname={`_position-absolute  ${styles.pulsatingDotForCopyStyles}`} />;
    }
    return null;
  };

  const onClickHandler = (itemId: FancyTextItemBottomBarOptions): void => {
    switch (itemId) {
      case FancyTextItemBottomBarOptions.TEXT:
        openPosterEditorEditFancyTextModal();
        break;
      case FancyTextItemBottomBarOptions.POSITION:
        openPosterEditorItemPositionModal();
        break;
      case FancyTextItemBottomBarOptions.EFFECT:
        openPosterEditorFancyTextEffectModal();
        break;
      case FancyTextItemBottomBarOptions.FONT:
        openPosterEditorFancyTextFontsModal();
        break;
      case FancyTextItemBottomBarOptions.COLORS:
        openPosterEditorFancyTextColorsModal();
        break;
      case FancyTextItemBottomBarOptions.TEXT_SHAPE:
        openPosterEditorFancyTextShapeModal();
        break;
      case FancyTextItemBottomBarOptions.DUPLICATE:
        duplicateActiveItems();
        break;
      case FancyTextItemBottomBarOptions.SHADOW:
        openPosterEditorShadowModal();
        break;
      case FancyTextItemBottomBarOptions.FLIP:
        openPosterEditorItemFlipModal();
        break;
      case FancyTextItemBottomBarOptions.STRETCH:
        openPosterEditorItemStretchModal();
        break;
      case FancyTextItemBottomBarOptions.COPY_STYLES:
        onCopyStylesClicked();
        break;
      case FancyTextItemBottomBarOptions.HYPERLINK:
        openPosterEditorHyperlinkModal();
        break;
      case FancyTextItemBottomBarOptions.OPACTIY:
        openPosterEditorItemOpacityModal();
        break;
      case FancyTextItemBottomBarOptions.ANIMATION:
        openItemAnimationModal();
        break;
      case FancyTextItemBottomBarOptions.LOCK:
        toggleLockStateForSelectedItem();
        break;
      case FancyTextItemBottomBarOptions.DELETE:
        deleteItemByID(activeItem.uid);
        break;
      default:
        break;
    }
  };

  return (
    <BottomBar
      isForEditorBottomBar={false}
      id="poster-editing-default-bottom-bar"
      onClick={onClickHandler}
      items={[
        {
          id: FancyTextItemBottomBarOptions.TEXT,
          icon: 'icon-text',
          text: window.i18next.t('pmwjs_text'),
        },
        {
          id: FancyTextItemBottomBarOptions.EFFECT,
          icon: 'icon-effects',
          text: window.i18next.t('pmwjs_effects'),
        },
        {
          id: FancyTextItemBottomBarOptions.FONT,
          icon: 'icon-font',
          text: window.i18next.t('pmwjs_font'),
        },
        {
          id: FancyTextItemBottomBarOptions.DUPLICATE,
          icon: 'icon-copy',
          text: window.i18next.t('pmwjs_duplicate'),
        },
        {
          id: FancyTextItemBottomBarOptions.POSITION,
          icon: 'icon-prints-stack',
          text: window.i18next.t('pmwjs_position'),
        },
        {
          id: FancyTextItemBottomBarOptions.COLORS,
          icon: 'icon-fill',
          text: window.i18next.t('pmwjs_colors'),
        },
        {
          id: FancyTextItemBottomBarOptions.TEXT_SHAPE,
          icon: 'icon-text-shape',
          text: window.i18next.t('pmwjs_text_shape'),
          textClasses: 'spacing-p-0',
        },
        {
          id: FancyTextItemBottomBarOptions.SHADOW,
          icon: 'icon-shadow',
          text: window.i18next.t('pmwjs_shadow'),
        },
        {
          id: FancyTextItemBottomBarOptions.FLIP,
          icon: 'icon-flip-horizontal',
          text: window.i18next.t('pmwjs_flip'),
        },
        {
          id: FancyTextItemBottomBarOptions.COPY_STYLES,
          icon: copiedStylesState === CopyStyleState.COPY ? 'icon-copy-style-brush' : 'icon-paste-style-brush',
          text: copiedStylesState === CopyStyleState.COPY ? window.i18next.t('pmwjs_copy_style') : window.i18next.t('pmwjs_paste_style'),
          isSelected: copiedStylesState === CopyStyleState.COPIED,
          tag: pulsatingDotForCopyStyles(),
        },
        {
          id: FancyTextItemBottomBarOptions.ANIMATION,
          icon: 'icon-animation',
          text: window.i18next.t('pmwjs_animation'),
        },
        {
          id: FancyTextItemBottomBarOptions.HYPERLINK,
          icon: 'icon-link',
          text: window.i18next.t('pmwjs_hyperlink'),
        },
        {
          id: FancyTextItemBottomBarOptions.OPACTIY,
          icon: 'icon-opacity',
          text: window.i18next.t('pmwjs_opacity'),
        },
        {
          id: FancyTextItemBottomBarOptions.LOCK,
          icon: isLocked ? 'icon-lock' : 'icon-unlock',
          text: isLocked ? window.i18next.t('pmwjs_unlock') : window.i18next.t('pmwjs_lock'),
        },
        {
          id: FancyTextItemBottomBarOptions.DELETE,
          icon: 'icon-delete',
          text: window.i18next.t('pmwjs_delete'),
          className: 'color-danger-100',
        },
      ]}
    />
  );
}
